import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";
import type { Property } from "@/types/property";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { useJsonLd } from "@/hooks/useJsonLd";
import { breadcrumbLd, realEstateListingLd } from "@/lib/structured-data";
import { propertyPath } from "@/lib/property-url";
import { cn } from "@/lib/utils";
import {
  formatArea,
  formatBathrooms,
  formatBedrooms,
  formatListingTitle,
  formatPrice,
  priceLabelFor,
} from "@/lib/format";
import { LeadForm } from "@/components/site/LeadForm";
import { useSiteConfig } from "@/lib/site-config";
import { useLiveProperties } from "@/hooks/useLiveProperties";
import { PropertyGallery } from "@/components/site/PropertyGallery";
import { rankPropertyImages } from "@/lib/property-images";

export function PropertyDetailPage() {
  const { id = "" } = useParams();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { lastEvent } = useLiveProperties();
  const siteConfig = useSiteConfig();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getProperty(id)
      .then((p) => !cancelled && setProperty(p))
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, lastEvent?.id === id ? lastEvent?.ts : 0]);

  const presentationTitle = property ? formatListingTitle(property) : "";
  const displayImages = useMemo(
    () => (property ? rankPropertyImages(property.images, property.id) : []),
    [property],
  );
  useSeo({
    title: property
      ? `${presentationTitle} | Hive Homes Dubai`
      : "Property | Hive Homes Dubai",
    description: property?.description
      ? property.description.slice(0, 160)
      : property
        ? `${presentationTitle} in ${property.community}, Dubai. View details, photos, and book a viewing with Hive Homes.`
        : "Hive Homes Dubai property detail.",
    // First image (when present) is the hero cover for this listing —
    // it is the most representative shot for a social card. Most
    // images come through our `/api/_img` proxy as a relative URL, so
    // we prefix with the absolute origin for crawlers that fetch the
    // tag without page context.
    ogImage: displayImages[0]
      ? absoluteUrl(displayImages[0])
      : undefined,
    canonicalPath: property ? propertyPath(property) : undefined,
  });

  useJsonLd(
    "property",
    property ? realEstateListingLd(property, displayImages) : null,
  );
  useJsonLd(
    "property-breadcrumb",
    property
      ? breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Properties", path: "/properties" },
          { name: presentationTitle, path: propertyPath(property) },
        ])
      : null,
  );

  if (loading) {
    // Skeleton that mirrors the real page structure — back link, headline
    // strip, gallery grid, and a sidebar block — instead of a centered
    // spinner. Reduces perceived load time and keeps the user oriented.
    return (
      <>
        <section className="border-b border-[var(--line)] bg-[var(--bg-2)]">
          <div className="container-wide py-5 md:py-6">
            <div className="h-3 w-32 animate-pulse bg-[var(--surface-2)]" />
          </div>
        </section>
        <section className="container-wide py-5 md:py-8">
          <div className="grid gap-3 sm:gap-4 md:grid-cols-[2fr_1fr]">
            <div className="aspect-[4/3] animate-pulse bg-[var(--surface-2)]" />
            <div className="grid gap-3 sm:gap-4">
              <div className="aspect-[4/3] animate-pulse bg-[var(--surface-2)]" />
              <div className="aspect-[4/3] animate-pulse bg-[var(--surface-2)]" />
            </div>
          </div>
        </section>
        <section className="border-t border-[var(--line)]">
          <div className="container-wide py-6 md:py-8">
            <div className="h-3 w-24 animate-pulse bg-[var(--surface-2)]" />
            <div className="mt-4 h-10 w-52 animate-pulse bg-[var(--surface-2)] md:h-12" />
            <div className="mt-3 h-4 w-1/2 animate-pulse bg-[var(--surface-2)]" />
          </div>
        </section>
      </>
    );
  }

  if (error || !property) {
    return (
      <div className="container-x flex flex-col items-center gap-4 py-32 text-center">
        <p className="font-display text-[40px] text-[var(--text)]">We can't find this one.</p>
        <p className="text-[var(--text-soft)]">
          {error ?? "It may have already found a new owner. Let us know if we can help you find something similar."}
        </p>
        <Link to="/properties" className="btn btn-ghost mt-4">
          <ArrowLeft className="size-4" />
          Back to properties
        </Link>
      </div>
    );
  }

  const labelTag = property.isExclusive
    ? "Exclusive"
    : property.listingType === "off-plan"
      ? "Off-plan"
      : property.listingType === "rent"
        ? "For rent"
        : "For sale";

  // The left article column is mostly conditional: below the specs row and
  // description sit Amenities and Off-plan blocks that are often absent. When
  // both are missing the column is short next to the tall sticky sidebar,
  // which previously left a large void under the description. Detecting that
  // "sparse" case lets us show the full description (real copy, not filler)
  // so the article — not the sidebar — drives the layout height.
  const buildingAmenities = Array.from(new Set(property.amenities));
  const parsedSpecifications = Array.from(new Set(property.features)).filter(
    (item) => !buildingAmenities.includes(item),
  );
  // Most agents don't write a spec list into the description, but the
  // Bayut-style layout the client asked for shows one on every listing —
  // fall back to bullets built from the structured feed fields.
  const propertySpecifications =
    parsedSpecifications.length > 0
      ? parsedSpecifications
      : [
          property.bedrooms > 0
            ? `Bedrooms: ${property.bedrooms}`
            : property.propertyType === "apartment"
              ? "Studio layout"
              : "",
          property.bathrooms > 0 ? `Bathrooms: ${property.bathrooms}` : "",
          property.area > 0 ? `Built-up Area: ${formatArea(property.area)}` : "",
          property.parking != null && property.parking > 0
            ? `Parking Spaces: ${property.parking}`
            : "",
          `Type: ${property.propertyType.charAt(0).toUpperCase() + property.propertyType.slice(1)}`,
          property.handoverDate && !Number.isNaN(Date.parse(property.handoverDate))
            ? `Handover: ${new Date(property.handoverDate).toLocaleDateString("en-AE", { month: "long", year: "numeric" })}`
            : "",
        ].filter(Boolean);
  const hasFeatures =
    buildingAmenities.length > 0 || propertySpecifications.length > 0;
  const hasOffPlanMeta =
    property.listingType === "off-plan" &&
    Boolean(property.developer || property.paymentPlan || property.handoverDate);
  const sparseColumn = !hasFeatures && !hasOffPlanMeta;
  const displayTitle = property.title?.trim() || presentationTitle;

  // Contact actions fall back to the company's own numbers so every
  // listing offers Call / WhatsApp / Email (reference: uniqueproperties.ae),
  // and the WhatsApp text carries the page URL the visitor came from.
  const contactPhone = property.agent.phone ?? siteConfig.phone.raw;
  const contactWhatsapp = property.agent.whatsapp ?? siteConfig.whatsapp.raw;
  const contactEmail = property.agent.email ?? siteConfig.email.raw;
  const whatsappText = `Hi Hive Homes, I have just visited your website page (${pageUrlFor(propertyPath(property))}) and would like to connect with you.`;

  return (
    <>
      <section className="border-b border-[var(--line)] bg-[var(--bg-2)]">
        <div className="container-wide py-5 md:py-6">
          <Link
            to="/properties"
            className="focus-ring inline-flex min-h-6 items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[var(--text-muted)] transition-colors duration-200 hover:text-[var(--cream)]"
          >
            <ArrowLeft className="size-3.5" aria-hidden />
            See all properties
          </Link>
        </div>
      </section>

      {/* Gallery leads, Bayut-style (client reference:
          bayut.com/property/details-15248732.html). */}
      <section className="border-b border-[var(--line)]">
        <div className="container-wide py-5 md:py-8">
          <PropertyGallery images={displayImages} alt={property.title} tag={labelTag} />
        </div>
      </section>

      {/* Summary band — price first, then location and the key figures.
          The listing title moves down to sit above the description. */}
      <section className="border-b border-[var(--line)]">
        <div className="container-wide py-6 md:py-8">
          <p className="eyebrow text-[var(--text-muted)]">{priceLabelFor(property)}</p>
          <p
            className="mt-2 font-display text-[32px] leading-none text-[var(--cream)] tabular-nums sm:text-[38px] md:text-[46px]"
            style={{ fontVariationSettings: '"opsz" 72' }}
          >
            {formatPrice(property.price, property.pricePeriod)}
          </p>
          <p className="mt-3 flex items-start gap-1.5 text-[14px] leading-[1.5] text-[var(--text-soft)] sm:text-[15px]">
            <MapPin className="mt-0.5 size-4 flex-none text-[var(--text-muted)]" strokeWidth={1.5} aria-hidden />
            <span className="min-w-0 break-words">
              {property.community}
              {property.subCommunity ? `, ${property.subCommunity}` : ""}
            </span>
          </p>
          <ul className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1.5 text-[14.5px] text-[var(--text)] sm:gap-x-4">
            <li>{formatBedrooms(property.bedrooms)}</li>
            {property.bathrooms > 0 && (
              <>
                <li aria-hidden className="text-[var(--text-muted)]">·</li>
                <li>{formatBathrooms(property.bathrooms)}</li>
              </>
            )}
            <li aria-hidden className="text-[var(--text-muted)]">·</li>
            <li>{formatArea(property.area)}</li>
          </ul>
        </div>
      </section>

      <section className="container-wide grid gap-8 py-9 md:grid-cols-12 md:items-start md:gap-12 md:py-12">
        <article className="md:col-span-7">
          {/* The agent's marketing headline, Bayut-proportioned: a modest
              heading above the description rather than a display hero. */}
          <h1
            className="max-w-[60ch] break-words font-display text-[22px] font-semibold leading-[1.3] text-[var(--text)] sm:text-[24px]"
            style={{ fontVariationSettings: '"opsz" 36' }}
          >
            {displayTitle}
          </h1>

          <section className="mt-7 md:mt-8">
            <p className="eyebrow text-[var(--cream)]">Description</p>
            {property.description ? (
              <Description text={property.description} clamp={!sparseColumn} />
            ) : (
              <p className="mt-4 text-[15px] text-[var(--text-soft)]">
                The full write-up is on its way. Contact us for details on this property.
              </p>
            )}
          </section>

          {propertySpecifications.length > 0 && (
            <section className="mt-9 md:mt-10">
              <p className="eyebrow text-[var(--cream)]">Property Specifications</p>
              <BulletList items={propertySpecifications} />
            </section>
          )}

          {buildingAmenities.length > 0 && (
            <section className="mt-9 md:mt-10">
              <p className="eyebrow text-[var(--cream)]">Building Amenities</p>
              <BulletList items={buildingAmenities} />
            </section>
          )}

          <section className="mt-9 border-y border-[var(--line)] py-7 md:mt-10">
            <p className="eyebrow text-[var(--cream)]">Property Information</p>
            <dl className="mt-5 grid grid-cols-2 gap-6 sm:grid-cols-3">
              <Meta
                label="Type"
                value={property.propertyType.charAt(0).toUpperCase() + property.propertyType.slice(1)}
              />
              <Meta
                label="Purpose"
                value={
                  property.listingType === "rent"
                    ? "For Rent"
                    : property.listingType === "off-plan"
                      ? "Off-Plan Sale"
                      : "For Sale"
                }
              />
              <Meta label="Reference no." value={property.reference} />
              {property.parking != null && property.parking > 0 && (
                <Meta label="Parking" value={`${property.parking}`} />
              )}
              {property.publishedAt && (
                <Meta
                  label="Added on"
                  value={new Date(property.publishedAt).toLocaleDateString("en-AE", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                />
              )}
            </dl>
          </section>

          {hasOffPlanMeta && (
            <section className="mt-9 border-y border-[var(--line)] py-7 md:mt-10">
              <p className="eyebrow text-[var(--cream)]">Off-plan</p>
              <dl className="mt-5 grid gap-6 sm:grid-cols-3">
                {property.developer && <Meta label="Developer" value={property.developer} />}
                {property.handoverDate && (
                  <Meta
                    label="Handover"
                    value={new Date(property.handoverDate).toLocaleDateString("en-AE", { month: "long", year: "numeric" })}
                  />
                )}
                {property.paymentPlan && <Meta label="Payment plan" value={property.paymentPlan} />}
              </dl>
            </section>
          )}
        </article>

        {/* Agent + enquiry sidebar */}
        <aside className="md:col-span-5">
          <div className="flex flex-col gap-6 rounded-[3px] border border-[var(--line-strong)] bg-[var(--surface)] p-5 sm:p-6 md:sticky md:top-32 md:gap-7 md:p-7">
            <p className="eyebrow text-[var(--cream)]">Your advisor</p>
            <div className="flex items-center gap-4">
              <div>
                <p
                  className="font-display text-[22px] text-[var(--text)]"
                  style={{ fontVariationSettings: '"opsz" 36' }}
                >
                  {property.agent.name}
                </p>
                <p className="meta uppercase tracking-[0.2em]">
                  {property.agent.title ?? "Hive team"}
                </p>
              </div>
            </div>

            {/* Bayut-style contact row — Call / WhatsApp / Email as
                proper buttons, not quiet text links. */}
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              {contactPhone && (
                <a
                  href={`tel:${contactPhone.replace(/\s+/g, "")}`}
                  className="btn btn-primary min-h-11 !px-3"
                >
                  <Phone className="size-4 flex-none" strokeWidth={1.6} aria-hidden />
                  Call
                </a>
              )}
              {contactWhatsapp && (
                <a
                  href={`https://wa.me/${contactWhatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(whatsappText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-11 items-center justify-center gap-2 bg-[#25D366] px-3 text-[13px] font-semibold uppercase tracking-[0.08em] text-white transition-opacity hover:opacity-90"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="size-4 flex-none" aria-hidden>
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                  </svg>
                  WhatsApp
                </a>
              )}
              {contactEmail && (
                <a
                  href={`mailto:${contactEmail}?subject=${encodeURIComponent(`Enquiry: ${presentationTitle} (Ref ${property.reference})`)}`}
                  className="btn btn-ghost min-h-11 !px-3"
                >
                  <Mail className="size-4 flex-none" strokeWidth={1.6} aria-hidden />
                  Email
                </a>
              )}
            </div>

            <div className="border-t border-[var(--line)] pt-5 md:pt-6">
              <p className="eyebrow text-[var(--cream)]">Book a viewing</p>
              <p className="mt-3 text-[14px] leading-[1.6] text-[var(--text-soft)]">
                Tell us when suits and we'll arrange a viewing — in person or by
                video call.
              </p>
              <div className="mt-5">
                <LeadForm
                  source="property-inquiry"
                  propertyId={property.id}
                  propertyReference={property.reference}
                  defaultMessage={`Hi Hive Homes, I'd like to enquire about ${presentationTitle}.`}
                  cta="Book a Viewing"
                  useFullContactFields={false}
                  messageLabel="Anything we should know?"
                />
              </div>
            </div>
          </div>
        </aside>
      </section>
    </>
  );
}

/**
 * Bayut-style bullet list (2-up mobile / 3-up desktop) used for the
 * "Property Specifications" and "Building Amenities" sections. Items
 * stay resilient to long strings.
 */
function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2.5 sm:grid-cols-3">
      {items.map((item) => (
        <li
          key={item}
          className="flex items-start gap-2.5 text-[13.5px] leading-[1.45] text-[var(--text)]"
        >
          <span
            aria-hidden
            className="mt-[7px] size-1.5 flex-none rounded-full bg-[var(--gold-bright)]"
          />
          <span className="min-w-0 break-words">{item}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Description with a tasteful, accessible expand/collapse. Long copy is
 * clamped to five lines so it never dominates the page; a real
 * `<button>` (aria-expanded) reveals the rest. Short copy renders as-is
 * with no control. The chevron rotation is transform-only.
 */
function Description({ text, clamp = true }: { text: string; clamp?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  // When the surrounding column is sparse (no Features / Off-plan), the
  // caller passes `clamp={false}` so the full write-up shows and the
  // article fills the height instead of stranding a void beside the
  // sticky sidebar. Otherwise long copy stays clamped behind "Read more".
  const isLong = clamp && text.length > 360;
  return (
    <div className="mt-4">
      <p
        className={cn(
          "max-w-[68ch] whitespace-pre-line text-[15px] leading-[1.7] text-[var(--text-soft)] sm:text-[15.5px]",
          isLong && !expanded && "line-clamp-5",
        )}
      >
        {text}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="focus-ring mt-3 inline-flex min-h-6 items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-bright)] transition-colors duration-200 hover:text-[var(--gold)]"
        >
          {expanded ? "Show less" : "Read more"}
          <ChevronDown
            className={cn(
              "size-3.5 transition-transform duration-200",
              expanded && "rotate-180",
            )}
            aria-hidden
          />
        </button>
      )}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="eyebrow text-[var(--text-muted)]">{label}</p>
      <p className="mt-2 text-[15px] text-[var(--text)]">{value}</p>
    </div>
  );
}

/**
 * Promote a relative image URL (e.g. `/api/_img?u=…`) to an absolute
 * one so OG / Twitter crawlers — which fetch the meta tag in isolation
 * — can still resolve the picture. Already-absolute URLs are returned
 * untouched.
 */
/**
 * Absolute page URL for share/WhatsApp texts. Prefers the visitor's real
 * origin, but falls back to the production domain during prerendering
 * and local development so a localhost link never ships in a message.
 */
function pageUrlFor(path: string): string {
  if (
    typeof window !== "undefined" &&
    !/^(localhost|127\.|0\.0\.0\.0|\[::1\])/.test(window.location.hostname)
  ) {
    return `${window.location.origin}${path}`;
  }
  return `https://hivehomes.ae${path}`;
}

function absoluteUrl(input: string): string {
  if (/^https?:\/\//i.test(input)) return input;
  if (typeof window === "undefined") return input;
  if (input.startsWith("/")) {
    return `${window.location.origin}${input}`;
  }
  return `${window.location.origin}/${input}`;
}
